/**
 * ---------------------------------------------------------------------------------------------------------------
 * SWYM WISHLIST — RE-SCAN APPENDED PRODUCT CARDS
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Swym injects its wishlist heart once, when it initialises, into the product cards that exist at that moment. It
 * does not observe the DOM afterwards, so every card added later has no heart.
 *
 * Collection pages append as you scroll, which makes that very visible. Measured on /collections/shoulder-bags,
 * identically on the live theme and on staging:
 *
 *     at page load          48 cards   48 hearts
 *     after scrolling      267 cards   48 hearts     <- 219 cards with no heart
 *     after a re-scan      267 cards  267 hearts
 *
 * It reads as arbitrary — some products have a heart, most do not — but it is purely positional: the cards present
 * on first paint get one, everything loaded afterwards does not. Sold-out state, pinned items, grid breakers and
 * viewport position were all checked and none of them correlate.
 *
 * Swym has no mutation observer of its own and no documented re-scan hook, but collectionsApi.initializeCollections
 * re-runs the injection and repairs every card. quick-view already leans on it after injecting "You may also like"
 * cards; this does the same for the collection grid.
 *
 * NOT PART OF THE A/B EXPERIMENT. The bug predates it and affects control identically, so this lives outside the
 * ab-* files, which are deleted when the experiment ends.
 */

(function () {
  'use strict';

  var LISTS = 'product-list, .product-list';

  /* Predictive search renders cards outside any product-list — measured ancestry:
     predictive-search > … > .predictive-search__products > div > .product-card_main
     so its dropdown showed four cards with no heart while the grid behind it had all 48. */
  var PANELS = 'predictive-search, .predictive-search';
  var CARD = '.product-card_main';
  var HEART = 'button.swym-wishlist-collections-v2';

  function canRescan() {
    return !!(
      window._swat &&
      window._swat.collectionsApi &&
      typeof window._swat.collectionsApi.initializeCollections === 'function'
    );
  }

  /** Cards Swym has not reached yet. Also the loop guard — see schedule(). */
  function unheartedCards() {
    return Array.prototype.filter.call(document.querySelectorAll(CARD), function (card) {
      return !card.querySelector(HEART) && getComputedStyle(card).display !== 'none';
    });
  }

  function rescan() {
    if (!canRescan()) return;

    try {
      window._swat.collectionsApi.initializeCollections(
        window._swat,
        true,
        window.Shopify && window.Shopify.theme && window.Shopify.theme.schema_name
      );
    } catch (error) {
      /* A wishlist heart is not worth breaking the grid over. */
    }
  }

  var queued = false;
  var verifyTimer = null;
  var attemptsLeft = 0;

  /* initializeCollections is asynchronous, so one call is not enough on its own. A batch that lands while a re-scan
     is still resolving is left unhearted, and once scrolling stops there are no further mutations to trigger
     another attempt — which is exactly how 27 of 267 cards stayed empty on the heavier v4 card.

     So each pass verifies itself: re-check shortly after, and go again if any visible card is still missing a heart.
     Budgeted rather than open-ended, because a card Swym legitimately declines to heart would otherwise retry
     forever. The budget resets whenever new cards are appended, so a long grid gets a fresh allowance per page. */
  var VERIFY_DELAY = 700;
  var MAX_ATTEMPTS = 8;

  var lastCardCount = -1;

  /* The budget is refreshed only when the number of cards has actually changed — never simply because something
     mutated. Predictive search has to be observed with subtree:true, since it swaps its markup on each keystroke,
     and that means Swym's own inserts are visible to the observer too. Refreshing on every mutation would let a
     card Swym declines to heart drive an endless rescan loop. */
  function refreshBudget() {
    var total = document.querySelectorAll(CARD).length;
    if (total === lastCardCount) return;

    lastCardCount = total;
    attemptsLeft = MAX_ATTEMPTS;
  }

  function attempt() {
    if (attemptsLeft <= 0) return;
    if (!unheartedCards().length) return;

    attemptsLeft--;
    rescan();
    verifyLater();
  }

  function verifyLater() {
    if (verifyTimer) clearTimeout(verifyTimer);

    verifyTimer = setTimeout(function () {
      verifyTimer = null;
      attempt();
    }, VERIFY_DELAY);
  }

  /**
   * Coalesced to one re-scan per frame: a page of appended cards arrives as a burst of mutations, and lazy images
   * and Swym itself keep mutating this subtree afterwards.
   *
   * The unheartedCards() check is what stops this recursing. Swym's own injection mutates the DOM, so a re-scan
   * would otherwise schedule the next one forever. Observing only direct children of the list (no subtree) means
   * Swym's inserts — which land inside a card — are not seen either, so the two guards are independent.
   */
  function schedule() {
    if (queued) return;
    queued = true;

    requestAnimationFrame(function () {
      queued = false;
      if (!unheartedCards().length) return;

      refreshBudget();
      attempt();
    });
  }

  var observer = null;

  function observeLists() {
    if (!('MutationObserver' in window)) return;
    if (!observer) observer = new MutationObserver(schedule);

    /* Lists: direct children only. Cards are direct children, and Swym's inserts land deeper, so its own
       mutations are invisible here — one of the two independent loop guards. */
    Array.prototype.forEach.call(document.querySelectorAll(LISTS), function (list) {
      if (list.dataset.swymRescanObserved) return;
      list.dataset.swymRescanObserved = '1';
      observer.observe(list, { childList: true });
    });

    /* Predictive search: subtree, because the results are replaced several levels down on every keystroke. The
       unhearted-cards check and the budget are what keep this bounded. */
    Array.prototype.forEach.call(document.querySelectorAll(PANELS), function (panel) {
      if (panel.dataset.swymRescanObserved) return;
      panel.dataset.swymRescanObserved = '1';
      observer.observe(panel, { childList: true, subtree: true });
    });
  }

  function start() {
    observeLists();

    /* Covers cards that were appended while Swym was still booting — filtering, a restored scroll position, or a
       fast "load more" before it finished. */
    schedule();
  }

  /* Swym replaces SwymCallbacks.push with a shim that runs late callbacks immediately, so this is correct whether
     Swym has already initialised or not. */
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(start);

  /* Collection pages swap the grid on filter and sort, and the theme editor re-renders sections in place; both
     replace the list element the observer was attached to. */
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('theme:section:updated', start);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) start();
  });
})();
